import { useCallback, useEffect, useState } from "react";
import { Instagram, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getStripeEnvironment } from "@/lib/stripe";
import {
  EXTRA_TEAM_PRICE,
  JUGEND_MAX,
  YOUTH_PACK_PRICE,
  defaultTeamTag,
  monthlyTotal,
  type TeamResult,
} from "@/components/signup/types";

interface TeamRow {
  id: string;
  bfv_team_id: string | null;
  team_name: string;
  team_tag: string;
  active: boolean;
}

interface Props {
  clubId: string;
  clubName: string;
  userId: string;
  youthPack: boolean;
  instagramEnabled: boolean;
  instagramConnected: boolean;
  selectedTier: string;
}

export default function TeamsSection({
  clubId,
  clubName,
  userId,
  youthPack,
  instagramEnabled,
  instagramConnected,
  selectedTier,
}: Props) {
  const isNewPlan = ["s", "m", "l", "jugend", "individuell"].includes(selectedTier);
  const { toast } = useToast();
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState<TeamResult[]>([]);
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [youth, setYouth] = useState(youthPack);
  const [connected, setConnected] = useState(instagramConnected);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("teams")
      .select("id, bfv_team_id, team_name, team_tag, active")
      .eq("account_id", userId)
      .order("created_at", { ascending: true });
    setTeams((data as TeamRow[] | null) ?? []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const syncSubscription = async (teamCount: number, withYouth: boolean) => {
    if (isNewPlan) return;
    const { error } = await supabase.functions.invoke("update-subscription-teams", {
      body: { environment: getStripeEnvironment(), teamCount, youthPack: withYouth },
    });
    if (error) {
      toast({
        title: "Abo konnte nicht angepasst werden",
        description: "Bitte versuch es später nochmal oder schreib uns an info@torflash.de.",
        variant: "destructive",
      });
    }
  };

  const openAdd = async () => {
    setAdding(true);
    if (available.length === 0) {
      try {
        const res = await fetch(
          `https://api.torflash.de/api/bfv-search?q=${encodeURIComponent(clubName)}`,
        );
        const data = await res.json();
        const club = (data.clubs || [])[0];
        setAvailable(club?.teams ?? []);
      } catch {
        setAvailable([]);
      }
    }
  };

  const newTotal = (count: number, withYouth: boolean) =>
    monthlyTotal(count, withYouth, instagramEnabled);

  const addTeam = async () => {
    if (isNewPlan) return;
    const team = available.find((t) => t.teamPermanentId === pending);
    if (!team) return;
    const count = teams.length + 1;
    if (!youth && count > 1 && !window.confirm(
      `Neuer Preis: ${newTotal(count, youth)} € / Monat (anteilig ab sofort). Hinzufügen?`,
    )) return;
    if (youth && count > JUGEND_MAX) {
      toast({ title: "Maximum erreicht", description: `Mit dem Jugend-Paket sind ${JUGEND_MAX} Mannschaften möglich.`, variant: "destructive" });
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("teams").insert({
      account_id: userId,
      bfv_team_id: team.teamPermanentId,
      team_name: team.teamName,
      team_tag: defaultTeamTag(team.teamName, team.category),
      active: true,
    });
    if (!error) {
      await syncSubscription(count, youth);
      await load();
      setAdding(false);
      setPending("");
    }
    setBusy(false);
  };

  const removeTeam = async (id: string) => {
    if (isNewPlan) return;
    const count = Math.max(1, teams.length - 1);
    if (!window.confirm(`Neuer Preis: ${newTotal(count, youth)} € / Monat. Mannschaft entfernen?`)) return;
    setBusy(true);
    await supabase.from("teams").delete().eq("id", id);
    await syncSubscription(count, youth);
    await load();
    setBusy(false);
  };

  const toggleYouth = async () => {
    if (isNewPlan) return;
    const next = !youth;
    if (!window.confirm(`Neuer Preis: ${newTotal(teams.length, next)} € / Monat. Umstellen?`)) return;
    setBusy(true);
    setYouth(next);
    await supabase.from("clubs").update({ youth_pack: next }).eq("id", clubId);
    await syncSubscription(teams.length, next);
    setBusy(false);
  };

  const saveTag = async (id: string, tag: string) => {
    setTeams((prev) => prev.map((t) => (t.id === id ? { ...t, team_tag: tag } : t)));
    await supabase.from("teams").update({ team_tag: tag }).eq("id", id);
  };

  const connectInstagram = async () => {
    setConnecting(true);
    const { data, error } = await supabase.functions.invoke("instagram-connect", {
      body: { accountId: userId, clubName },
    });
    setConnecting(false);
    if (error || !data?.url) {
      toast({
        title: "Instagram-Verbindung nicht möglich",
        description: "Bitte versuch es gleich nochmal oder schreib uns an info@torflash.de.",
        variant: "destructive",
      });
      return;
    }
    window.open(data.url as string, "_blank", "noopener");
  };

  useEffect(() => {
    if (!instagramEnabled || connected) return;
    const id = setInterval(async () => {
      const { data } = await supabase
        .from("clubs")
        .select("instagram_connected")
        .eq("id", clubId)
        .maybeSingle();
      if (data?.instagram_connected) setConnected(true);
    }, 10000);
    return () => clearInterval(id);
  }, [instagramEnabled, connected, clubId]);

  return (
    <>
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight text-foreground">
          Mannschaften
        </h2>
        {!isNewPlan && <p className="mt-1 text-sm text-muted-foreground">
          Erste Mannschaft im Grundpreis, jede weitere {EXTRA_TEAM_PRICE} € / Monat
          {youth ? ` (Jugend-Paket aktiv: ${YOUTH_PACK_PRICE} € pauschal)` : ""}.
        </p>}

        {loading ? (
          <Loader2 className="mt-4 h-5 w-5 animate-spin text-primary" />
        ) : (
          <div className="mt-4 space-y-2">
            {teams.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-3 rounded-lg border border-border p-3"
              >
                <span className="flex-1 text-sm font-medium text-foreground">{t.team_name}</span>
                <Input
                  value={t.team_tag}
                  onChange={(e) => saveTag(t.id, e.target.value)}
                  className="h-8 w-[130px] text-sm"
                />
                {!isNewPlan && <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy || teams.length <= 1}
                  onClick={() => removeTeam(t.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>}
              </div>
            ))}
            {teams.length === 0 && (
              <p className="text-sm text-muted-foreground">Noch keine Mannschaften angelegt.</p>
            )}
          </div>
        )}

        {isNewPlan ? <p className="mt-4 text-sm text-muted-foreground">Für Änderungen an Mannschaften oder Paket schreib uns an <a className="text-primary underline" href="mailto:info@torflash.de?subject=Paketwechsel">info@torflash.de</a>.</p> : adding ? (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              value={pending}
              onChange={(e) => setPending(e.target.value)}
              className="h-9 min-w-[220px] rounded-md border border-border bg-background px-2 text-sm"
            >
              <option value="">Mannschaft wählen…</option>
              {available
                .filter((a) => !teams.some((t) => t.bfv_team_id === a.teamPermanentId))
                .map((a) => (
                  <option key={a.teamPermanentId} value={a.teamPermanentId}>
                    {a.teamName}
                  </option>
                ))}
            </select>
            <Button size="sm" variant="hero" disabled={!pending || busy} onClick={addTeam}>
              Hinzufügen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
              Abbrechen
            </Button>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={openAdd}>
              <Plus className="h-3.5 w-3.5" /> Mannschaft hinzufügen
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={toggleYouth}>
              {youth ? "Jugend-Paket beenden" : `Jugend-Paket aktivieren (+${YOUTH_PACK_PRICE} €)`}
            </Button>
          </div>
        )}

        {!isNewPlan && <p className="mt-3 text-sm font-medium text-foreground">
          Aktuell: {newTotal(Math.max(1, teams.length), youth)} € / Monat
        </p>}
      </div>

      {instagramEnabled && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight text-foreground">
            Instagram
          </h2>
          {connected ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Verbunden. Eure Grafiken laufen automatisch auf eurem Instagram.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                Danach laufen eure Grafiken automatisch auf eurem Instagram.
              </p>
              <Button
                variant="hero"
                size="sm"
                className="mt-4 gap-1.5"
                disabled={connecting}
                onClick={connectInstagram}
              >
                {connecting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Instagram className="h-3.5 w-3.5" />
                )}
                Instagram verbinden
              </Button>
            </>
          )}
        </div>
      )}
    </>
  );
}
